import { useEffect, useMemo, useState } from 'react'
import { Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { toast } from 'react-toastify'
import Navbar from '../../../components/navbar/navbar'
import removeNonCharacters from '../../../utils/removeNonCharacters'

import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  ShareFill,
  TrashFill,
} from 'react-bootstrap-icons'
import { CustomButton } from '../../../components/CustomButton'
import { CustomSpinner } from '../../../components/CustomSpinner'

import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { isLojaBelaHome } from '../../../config/globalVariables'
import { useOrderTable } from '../../../hooks/useOrderTable'
import PessoasService from '../../../services/database/PessoasService'

export function Clientes() {
  const [customers, setCustomers] = useState([])
  const [termSearch, setTermSearch] = useState('pes_razao')
  const [search, setSearch] = useState('')

  const [pageCount, setPageCount] = useState(0)
  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [isCustomersLoading, setIsCustomersLoading] = useState(true)

  const endOffset = itemOffset + itemsPerPage

  const navigate = useNavigate()

  const {
    sortedArray: sortedCustomers,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(customers, 'cod_pessoa')

  const filteredCustomers = useMemo(() => {
    let customersFiltered = []

    // Escolha o campo correto para a pesquisa com base no `termSearch`
    if (['pes_razao', 'pes_logradouro', 'pes_fantasia'].includes(termSearch)) {
      customersFiltered = sortedCustomers.filter((customer) => {
        const valueToSearch = String(customer[termSearch] || '').toLowerCase()
        return valueToSearch.includes(String(search).toLowerCase())
      })
    } else {
      // Pesquisa padrão (pes_cnpj_cpf)
      customersFiltered = sortedCustomers.filter((customer) => {
        const valueFormatted = removeNonCharacters(customer.pes_cnpj_cpf || '')
        return String(valueFormatted).includes(search)
      })
    }

    // Resetar a página se houver poucas correspondências
    if (search.length > 0 && customersFiltered.length <= 1) {
      setItemOffset(0)
    }

    // Atualizar o total de páginas
    setPageCount(Math.ceil(customersFiltered.length / itemsPerPage))

    // Retornar o segmento de itens da página atual
    return customersFiltered.slice(itemOffset, endOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endOffset,
    itemOffset,
    search,
    sortedCustomers,
    orderType.field,
    orderType.type,
  ])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % customers.length

    setItemOffset(newOffset)
  }

  async function loadCustomers() {
    setIsCustomersLoading(true)

    try {
      const data = await PessoasService.getAll()

      setCustomers(data)
      console.log(data)
    } catch (error) {
      console.log(`Houve um erro ao listar os clientes: ${error}`)
      toast.error('Houve um erro ao listar os clientes!')
    } finally {
      setIsCustomersLoading(false)
    }
  }

  useEffect(() => {
    loadCustomers()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page p-4">
        <h1>Listagem de Clientes</h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 10,
          }}
        >
          <section>
            <p>Clientes por página</p>
            <Form.Select
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
              }}
              style={{
                width: '200px',
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </section>

          <section>
            <p>Termo de Pesquisa</p>

            <div
              style={{
                display: 'flex',
              }}
            >
              <Form.Select
                value={termSearch}
                onChange={(e) => {
                  setTermSearch(e.target.value)
                }}
                style={{
                  width: '200px',
                }}
              >
                <option value="pes_razao">Razão Social</option>
                <option value="pes_cnpj_cpf">CPF | CNPJ</option>
                <option value="pes_fantasia">Nome Fantasia</option>
                <option value="pes_logradouro">Logradouro</option>
              </Form.Select>
            </div>
          </section>

          <section>
            <p>Campo de Pesquisa</p>

            <div
              style={{
                display: 'flex',
              }}
            >
              <Form.Control
                value={search}
                placeholder="Pesquise..."
                onChange={(e) => {
                  setSearch(e.target.value)
                  setItemOffset(0)
                }}
                style={{
                  width: '400px',
                }}
              />
            </div>
          </section>

          <CustomButton
            title="Cadastrar"
            color="#088c04"
            onClick={() => {
              navigate('/clientes/novo')
            }}
          >
            <PlusCircleFill />
          </CustomButton>

          <CustomButton
            title="Recarregar"
            color="#007FFF"
            onClick={loadCustomers}
          >
            <ArrowClockwise />
          </CustomButton>
        </div>

        {isCustomersLoading ? (
          <div
            style={{
              marginTop: 50,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CustomSpinner size={80} />
          </div>
        ) : (
          <div>
            <Table
              striped
              bordered
              hover
              responsive
            >
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('cod_pessoa')}>
                    <span>Código</span>
                    <CaretUp color={check('cod_pessoa', 'asc')} />
                    <CaretDown color={check('cod_pessoa', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('pes_razao')}>
                    <span>Razão Social</span>
                    <CaretUp color={check('pes_razao', 'asc')} />
                    <CaretDown color={check('pes_razao', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('pes_fantasia')}>
                    <span>Nome Fantasia</span>
                    <CaretUp color={check('pes_fantasia', 'asc')} />
                    <CaretDown color={check('pes_fantasia', 'desc')} />
                  </th>

                  <th>CPF | CNPJ</th>
                  <th>Telefone</th>

                  <th onClick={() => handleOrganize('pes_logradouro')}>
                    <span>Logradouro</span>
                    <CaretUp color={check('pes_logradouro', 'asc')} />
                    <CaretDown color={check('pes_logradouro', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('pes_cidade')}>
                    <span>Cidade</span>
                    <CaretUp color={check('pes_cidade', 'asc')} />
                    <CaretDown color={check('pes_cidade', 'desc')} />
                  </th>

                  <th>UF</th>
                  <th>Ações</th>
                </tr>
              </thead>

              <tbody>
                {filteredCustomers.map((customer) => (
                  <Customer
                    key={customer.cod_pessoa}
                    customer={customer}
                    loadCustomers={loadCustomers}
                  />
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </div>
        )}
      </div>
    </>
  )
}

function Customer({ customer, loadCustomers }) {
  const [isDeleteCustomerLoading, setIsDeleteCustomerLoading] = useState(false)
  const [isExportLoading, setIsExportLoading] = useState(false)

  const navigate = useNavigate()

  async function handleExportOrder(customerId) {
    setIsExportLoading(true)
    try {
      await PessoasService.exportToMatriz(customerId)

      toast.success('Cliente exportado com sucesso!')
    } catch (error) {
      console.log(`Erro ao exportar o cliente: ${error}`)

      toast.error('Erro ao exportar o cliente, tente novamente mais tarde!')
    } finally {
      setIsExportLoading(false)
    }
  }

  function handleUpdateCustomer(customerId) {
    navigate(`/clientes/editar/${customerId}`)
  }

  async function handleDeleteCustomer(customerToDelete) {
    const { isConfirmed } = await Swal.fire({
      titleText: `Deseja deletar ${customerToDelete.pes_razao}?`,
      icon: 'warning',
      text: 'Essa ação é irreversível!',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Deletar',
      confirmButtonColor: '#e70f0f',
      cancelButtonText: 'Cancelar',
    })

    if (isConfirmed) {
      setIsDeleteCustomerLoading(true)

      try {
        await PessoasService.delete(customer.cod_pessoa)

        toast.success('Cliente deletado com sucesso!')

        loadCustomers()
      } catch (error) {
        console.log(`Erro ao deletar cliente: ${error}`)
        toast.error(`Erro ao deletar cliente!`)
      } finally {
        setIsDeleteCustomerLoading(false)
      }
    }
  }

  return (
    <tr>
      <td>{customer.cod_pessoa}</td>
      <td>{customer.pes_razao}</td>
      <td>{customer.pes_fantasia} </td>
      <td>{customer.pes_cnpj_cpf} </td>
      <td>{customer.pes_telefone}</td>
      <td>{customer.pes_logradouro}</td>
      <td>{customer.pes_cidade}</td>
      <td>{customer.pes_uf}</td>
      <td>
        <div
          style={{
            display: 'flex',
          }}
        >
          {isLojaBelaHome && (
            <OverlayTrigger overlay={<Tooltip>Exportar para Matriz</Tooltip>}>
              <CustomButton
                color="#0641d4"
                onClick={() => handleExportOrder(customer.cod_pessoa)}
                isLoading={isExportLoading}
              >
                <ShareFill />
              </CustomButton>
            </OverlayTrigger>
          )}

          <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
            <CustomButton
              color="#ec630a"
              onClick={() => handleUpdateCustomer(customer.cod_pessoa)}
            >
              <PencilFill />
            </CustomButton>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
            <CustomButton
              color="#e70f0f"
              onClick={() => handleDeleteCustomer(customer)}
              isLoading={isDeleteCustomerLoading}
            >
              <TrashFill />
            </CustomButton>
          </OverlayTrigger>
        </div>
      </td>
    </tr>
  )
}
